import axios, { AxiosError } from 'axios';
import { RentPin, RentalListing, RentalSearch } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Derives the admin-API base (same host, no /api suffix) for public log-error calls
const ADMIN_BASE_URL = (() => {
  const url = process.env.REACT_APP_API_URL;
  if (url) return url.replace(/\/api\/?$/, '');
  return 'http://localhost:5050';
})();

export function logClientError(
  error_type: string,
  error_message: string,
  details: Record<string, unknown> = {}
): void {
  axios.post(`${ADMIN_BASE_URL}/log-error`, { error_type, error_message, details }).catch(() => {});
}

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

client.interceptors.response.use(
  (res) => res,
  (err: AxiosError<{ error: string }>) => {
    const message = err.response?.data?.error || err.message || 'Something went wrong';
    return Promise.reject(new Error(message));
  }
);

// ─── Request types ──────────────────────────────────────────────────────────

export interface CreateRentPinRequest {
  location: { coordinates: [number, number] };
  address: string;
  city: string;
  postalCode?: string;
  bedrooms?: number;
  monthlyRent: number;
  furnishing?: 'Furnished' | 'Semi-Furnished' | 'Unfurnished';
  residentsType?: 'Family' | 'Bachelor' | 'Mixed';
  depositPaid?: boolean;
  petsAllowed?: boolean;
  petsAllowedStatus?: string;
  squareFootage?: number;
  propertyType?: string;
  baths?: string;
  amenities?: string[];
  utilitiesIncluded?: string[];
  transitDistance?: string;
  schoolRating?: number;
  description?: string;
  email?: string;
  phone?: string;
  anonymousSubmission?: boolean;
  consentAcceptedAt?: string;
}

export interface CreateRentalSearchRequest {
  searchType: 'LookingForRoom' | 'LookingForWhole';
  propertyType?: string;
  budget: number;
  budgetPerRoom?: number;
  bhkPreference?: number;
  moveInTimeline: string;
  foodPreference?: string;
  petFriendly?: boolean;
  okayWithSmoker?: boolean;
  userGender?: string;
  youAre?: string;
  comfortableWith?: string;
  desiredFlatmateGender?: string;
  employmentStatus?: string;
  smokingHabits?: string;
  bathType?: string;
  amenities?: string[];
  utilitiesIncluded?: string[];
  petsAllowedStatus?: string;
  transitDistance?: string;
  schoolRating?: number;
  furnishing?: string;
  description?: string;
  lifestyle?: string;
  email: string;
  phone?: string;
  city: string;
  address?: string;
  location?: { coordinates: [number, number] };
  consentAcceptedAt?: string;
}

export interface CreateRentalListingRequest {
  listingType: 'RentingRoom' | 'RentingWhole';
  propertyType?: string;
  budget: number;
  budgetPerRoom?: number;
  bhk?: number;
  availability?: string;
  foodPreference?: string;
  desiredTenantGender?: string;
  youAre?: string;
  comfortableWith?: string;
  employmentStatus?: string;
  smokingHabits?: string;
  baths?: string;
  bathType?: string;
  furnishing?: string;
  amenities?: string[];
  utilitiesIncluded?: string[];
  petsAllowedStatus?: string;
  transitDistance?: string;
  schoolRating?: number;
  description?: string;
  lifestyle?: string;
  location: { coordinates: [number, number] };
  address: string;
  city: string;
  email: string;
  phone?: string;
  consentAcceptedAt?: string;
}

export interface ReportRequest {
  rentPinId: string;
  reason: 'IncorrectPrice' | 'InvalidAddress' | 'InappropriateContent' | 'Other';
  description?: string;
  reporterEmail?: string;
}

export interface RentsByCity {
  data: RentPin[];
  total: number;
  hasMore: boolean;
}

export interface ListingsResult {
  data: RentalListing[];
  total: number;
  hasMore: boolean;
}

// ─── API methods ─────────────────────────────────────────────────────────────

export const rentalApi = {
  createRentPin: (data: CreateRentPinRequest) =>
    client.post<{ success: boolean; data: RentPin; deleteToken: string }>('/rents/create', data),

  getRentPinById: (id: string) =>
    client.get<{ success: boolean; data: RentPin }>(`/rents/${id}`),

  getRentsNearby: (longitude: number, latitude: number, maxDistance = 5000) =>
    client.get<{ success: boolean; data: RentPin[] }>('/rents/nearby', {
      params: { longitude, latitude, maxDistance },
    }),

  getRentsByCity: (city: string, filters?: { bedrooms?: number; minRent?: number; maxRent?: number; skip?: number; limit?: number }) =>
    client.get<RentsByCity>('/rents/city', { params: { city, ...filters } }),

  getAverageRents: (city: string, bedrooms?: number) =>
    client.get('/rents/averages', { params: { city, bedrooms } }),

  reportRent: (data: ReportRequest) =>
    client.post('/rents/report', data),

  checkRentPinOwnership: (id: string) =>
    client.get<{ isOwner: boolean }>(`/rents/${id}/owner-check`),

  updateRentPin: (id: string, data: Partial<Omit<CreateRentPinRequest, 'location' | 'address' | 'city' | 'postalCode' | 'anonymousSubmission' | 'email' | 'phone' | 'consentAcceptedAt'>>) =>
    client.patch<{ success: boolean; data: import('../types').RentPin }>(`/rents/${id}`, data),

  deleteRentPinByIp: (id: string) =>
    client.delete(`/rents/${id}`),

  deleteRentPinByToken: (id: string, deleteToken: string) =>
    client.delete(`/rents/${id}`, { headers: { 'x-delete-token': deleteToken } }),

  createRentalSearch: (data: CreateRentalSearchRequest) =>
    client.post<{ success: boolean; data: { id: string }; deleteToken: string }>('/rental/search', data),

  searchListings: (filters?: { city?: string; propertyType?: string; budget?: number; skip?: number; limit?: number }) =>
    client.get<ListingsResult>('/rental/search', { params: filters }),

  createRentalListing: (data: CreateRentalListingRequest) =>
    client.post<{ success: boolean; data: { id: string }; deleteToken: string }>('/rental/listing', data),

  getRentalListingById: (id: string) =>
    client.get<{ success: boolean; data: RentalListing }>(`/rental/listing/${id}`),

  getRentalSearchById: (id: string) =>
    client.get<{ success: boolean; data: RentalSearch }>(`/rental/search/${id}`),

  deleteRentalSearch: (id: string, deleteToken: string) =>
    client.delete(`/rental/search/${id}`, { headers: { 'x-delete-token': deleteToken } }),

  deleteRentalListing: (id: string, deleteToken: string) =>
    client.delete(`/rental/listing/${id}`, { headers: { 'x-delete-token': deleteToken } }),

  expressListingInterest: (id: string, data: { name: string; email: string; phone?: string; message?: string }) =>
    client.post<{ success: boolean }>(`/rental/listing/${id}/interest`, data),

  reportListing: (id: string, data: { reason: string; description?: string; reporterEmail?: string }) =>
    client.post<{ success: boolean }>(`/rental/listing/${id}/report`, data),
};

export default rentalApi;
